import { Link } from 'react-router-dom';
import { CaretRight, Warning } from '@phosphor-icons/react';

import { MoneyDisplay } from '../common/MoneyDisplay';
import { HelpTooltip } from '../common/HelpTooltip';
import { HeroProgressBar } from '../common/HeroProgressBar';
import { CycleHeroShell } from '../common/CycleHeroShell';
import pt from '../../locales/pt';
import { formatEUR } from '../../utils/money';
import { formatSelfiePeriodLabel } from '../../utils/selfiePeriod';
import { cn } from '../../utils/cn';
import { paths } from '../../routes/paths';
import {
  allocationAssignedPct,
  allocationCompletionHint,
  buildAllocationSegments,
} from './budgetAllocationUtils';

/**
 * Layer 1 — «Já atribuíste €X de €Y».
 *
 * `.pen` → `ds/card/budget-allocation-hero` (normal) e `--excesso` (âmbar):
 * mesma arquitectura de composição que `ExpensesCycleHero` — `CycleHeroShell`
 * (cycle-nav + cartão métrica + rodapé) com um `HeroCard` como métrica.
 *
 * Os estados "sem rendimento" (`!hasIncome`) e "1º ciclo completo"
 * (`firstCycle && state === 'complete'`) não têm hero cheio no `.pen` (ver
 * `Orçamento · Sem categorias`, que reutiliza o cartão neutro antigo) —
 * mantêm-se inalterados aqui.
 */
export function BudgetAllocationHero({
  overview,
  firstCycle = false,
  activeCycle,
  className,
}) {
  const showSummary = Boolean(
    overview?.show_planning_summary
      || overview?.has_income
      || overview?.is_historical_cycle,
  );
  if (!showSummary || !overview) return null;

  const groups = overview.groups || [];
  const income = Number(overview.cycle_income_eur ?? 0);
  const hasIncome = Boolean(overview.has_income) && income > 0;
  const { totalAssigned } =
    buildAllocationSegments({
      groups,
      cycleIncomeEur: income,
    });
  const { state, remaining, excess } = allocationCompletionHint({
    totalAssigned,
    income,
  });
  const assignedPct = allocationAssignedPct({ totalAssigned, income });

  if (!hasIncome) {
    return (
      <section
        className={cn(
          'rounded-ds-xl border border-border bg-card p-space-5 shadow-[0_6px_16px_rgb(var(--glass-shadow)/0.08)]',
          className,
        )}
      >
        <p className="type-body-sm text-muted-foreground">
          {pt.budget.method.groupedHint}
        </p>
      </section>
    );
  }

  if (firstCycle && state === 'complete') {
    return (
      <section
        className={cn(
          'rounded-ds-xl border border-border bg-card p-space-5 shadow-[0_6px_16px_rgb(var(--glass-shadow)/0.08)]',
          className,
        )}
      >
        <div className="flex items-baseline justify-between gap-3">
          <p className="leading-[1.15] tracking-tight">
            <MoneyDisplay value={totalAssigned} className="type-display-metric text-foreground" />
            <span className="ml-1.5 type-title-card text-muted-foreground">
              {pt.budget.allocationHero.of}{' '}
              <MoneyDisplay
                value={income}
                className="type-title-card font-semibold text-muted-foreground"
              />
            </span>
          </p>
          <span className="type-chip font-semibold text-sage-on-tint">100%</span>
        </div>
        <p className="mt-0.5 type-body-sm text-muted-foreground">
          {pt.budget.allocationHero.firstCycleReady}
        </p>
      </section>
    );
  }

  const isOver = state === 'over';
  const fillPct = Math.max(0, Math.min(100, assignedPct));

  const periodLabel =
    activeCycle?.start && activeCycle?.end
      ? formatSelfiePeriodLabel(activeCycle.start, activeCycle.end)
      : '';

  const scaleRightText = isOver
    ? pt.budget.allocationHero.overPlan.replace('{amount}', formatEUR(excess ?? 0))
    : pt.budget.allocationHero.remainingToAssign.replace(
        '{amount}',
        formatEUR(remaining ?? 0),
      );

  const linkLabel = isOver
    ? pt.budget.allocationHero.adjustExcessLink
    : pt.budget.allocationHero.viewExpensesLink;

  const metric = (
    <>
      <div className="flex min-w-0 flex-col gap-space-1">
        <div className="flex min-w-0 items-center gap-space-2 text-sage-50/80">
          <span className="type-body-sm min-w-0 flex-1">
            {pt.budget.allocationHero.label}
          </span>
          {isOver ? (
            <Warning className="h-4 w-4 shrink-0" aria-hidden />
          ) : (
            <HelpTooltip
              appearance="plain"
              label={pt.budget.allocationHero.tooltip}
              content={pt.budget.allocationHero.tooltip}
            />
          )}
        </div>

        <p className="type-display-hero text-sage-50">{formatEUR(totalAssigned)}</p>

        <p className="type-body-sm text-sage-50/80">
          {pt.budget.allocationHero.ofCycle.replace('{amount}', formatEUR(income))}
        </p>
      </div>

      <div className="flex min-w-0 flex-col gap-space-2">
        <HeroProgressBar
          variant={isOver ? 'heroDanger' : 'filled'}
          percentage={fillPct}
          ariaLabel={pt.budget.allocationHero.label}
        />
        <div className="flex items-center justify-between gap-space-2 text-sage-50/80">
          <span className="type-label">
            {pt.budget.allocationHero.assignedPctLabel.replace(
              '{pct}',
              String(Math.round(assignedPct)),
            )}
          </span>
          <span className="type-label">{scaleRightText}</span>
        </div>

        {isOver ? (
          <div className="flex justify-start">
            <span className="inline-flex max-w-full items-center gap-space-2 rounded-ds-full bg-warning-500 px-space-3 py-space-1">
              <Warning className="h-3.5 w-3.5 shrink-0 text-stone-900" aria-hidden />
              <span className="type-label min-w-0 text-stone-900">
                {pt.budget.allocationHero.hintOver.replace(
                  '{amount}',
                  formatEUR(excess ?? 0),
                )}
              </span>
            </span>
          </div>
        ) : null}
      </div>
    </>
  );

  const footer = (
    <div className="flex justify-center">
      <Link
        to={paths.expenses}
        className="type-body-sm inline-flex items-center gap-space-1 font-semibold text-sage-on-tint transition-colors hover:text-sage-700"
      >
        {linkLabel}
        <CaretRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
      </Link>
    </div>
  );

  return (
    <CycleHeroShell
      chipLabel={pt.budget.allocationHero.cycleChip.replace('{period}', periodLabel)}
      prevAriaLabel={pt.budget.cycle.prev}
      nextAriaLabel={pt.budget.cycle.next}
      variant={isOver ? 'heroDanger' : 'hero'}
      className={className}
      footer={footer}
    >
      {metric}
    </CycleHeroShell>
  );
}

export default BudgetAllocationHero;
